import DialogTitle from "@mui/material/DialogTitle"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import Button from "@mui/material/Button"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormControl from "@mui/material/FormControl"

import DateRangeIcon from "@mui/icons-material/DateRange"

import {
  Box,
  CircularProgress,
  DialogActions,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { SubmitHandler, useForm } from "react-hook-form"
import { useGetSelectQuery } from "../../../api/InterviewsApi"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import { keycloakInstance } from "../../../../../shared/libs/keycloack/config"
import { baseUrl } from "../../../../../shared/api/baseQuery"
import { enqueueSnackbar } from "notistack"
import { useState } from "react"

type Type = "hiring" | "payment"

export interface IWeekStatisticDialog {
  type: Type
  startDate: string
  endDate: string
  department: string
}

const WeekStatisticDialog = ({ open, setOpen }: any) => {
  const [loading, setLoading] = useState(false)

  const selects = useGetSelectQuery()

  const departments = selects?.data.filterOptions.departmentSMKT ?? []

  const { handleSubmit, register, watch, formState, setValue } =
    useForm<IWeekStatisticDialog>({
      defaultValues: {
        type: "hiring",
        startDate: "",
        endDate: "",
        department: "",
      },
      mode: "onChange",
    })

  const onSubmit: SubmitHandler<IWeekStatisticDialog> = (data) => {
    setLoading(true)

    fetch(`${baseUrl}/download_xlsx`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${keycloakInstance.token}}`,
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `Отчёт за ${dayjs(data.startDate).format(
          "DD.MM.YYYY",
        )}-${dayjs(data.endDate).format("DD.MM.YYYY")}.xlsx`
        link.click()
        URL.revokeObjectURL(url)
      })
      .finally(() => {
        setLoading(false)
        setOpen({ ...open, weekStatisticDialog: false })
      })
      .catch((error) => {
        enqueueSnackbar("Ошибка при скачивании файла", {
          variant: "error",
        })
      })
  }

  const { errors } = formState

  return (
    <Dialog
      sx={{
        ".MuiDialog-paper": {},
        ".MuiDialogContent-root": {},
        ".MuiDialogActions-root": { px: "24px", pb: "40px" },
      }}
      maxWidth={"lg"}
      open={open.weekStatisticDialog}
      keepMounted
    >
      <DialogTitle sx={{ fontWeight: "bold", mt: "20px" }}>
        Скачивание
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {loading ? (
            <Stack
              sx={{ width: "100%", height: "257px" }}
              direction="column"
              alignItems="center"
              spacing={"10px"}
            >
              <Stack
                direction="column"
                alignItems="center"
                sx={{ display: "flex" }}
                paddingBottom="40px"
                spacing="110px"
              >
                <Typography fontSize="18px">
                  Идет подготовка файла для загрузки. Не закрывайте вкладку.
                </Typography>
                <CircularProgress size={80} />
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={"12px"} direction={"column"}>
              <FormControl>
                <RadioGroup
                  row
                  value={watch("type")}
                  onChange={(event) =>
                    setValue("type", event.target.value as Type)
                  }
                >
                  <FormControlLabel
                    value="hiring"
                    control={<Radio />}
                    label="Отчёт по найму"
                  />
                  <FormControlLabel
                    value="payment"
                    control={<Radio />}
                    label="Отчёт по оплатам"
                  />
                </RadioGroup>
              </FormControl>

              {watch("type") === "hiring" ? (
                <Stack direction={"column"} spacing={"5px"}>
                  <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                    Направление
                  </Typography>
                  <TextField
                    size="small"
                    label="Направление"
                    inputProps={register("department", {
                      required:
                        watch("type") === "hiring"
                          ? "Это поле обязательно для заполнения"
                          : false,
                    })}
                    fullWidth
                    value={watch("department")}
                    select
                    error={!!errors.department}
                    helperText={errors.department?.message}
                  >
                    {departments?.map((option: any) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              ) : (
                <Box sx={{ height: "66px" }}></Box>
              )}
              <Stack direction={"column"} spacing={"5px"}>
                <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                  {watch("type") === "hiring"
                    ? "Дата изменения"
                    : "Дата создания карточки"}
                </Typography>
                <Stack
                  alignItems={"center"}
                  justifyContent={"center"}
                  direction={"row"}
                  spacing={"5px"}
                >
                  <DatePicker
                    slots={{
                      openPickerIcon: DateRangeIcon,
                    }}
                    sx={{
                      ".MuiIconButton-root": {
                        color: "rgba(139, 139, 139, 1)",
                      },
                      minHeight: "84px",
                      width: "266px",
                    }}
                    value={dayjs(watch("startDate")) || null}
                    localeText={{
                      toolbarTitle: "ЧЧ.ММ.ГГГГ",
                      fieldDayPlaceholder: () => "ДД",
                      fieldMonthPlaceholder: () => "ММ",
                      fieldYearPlaceholder: () => "ГГГГ",
                    }}
                    onChange={(e: Dayjs | null) => {
                      dayjs(e).isValid() &&
                        setValue(
                          "startDate",
                          dayjs(e).format("YYYY-MM-DDTHH:mm:ssZ"),
                        )
                    }}
                    format="DD.MM.YYYY"
                    slotProps={{
                      textField: {
                        inputProps: register("startDate", {
                          required: "Это поле обязательно для заполнения",
                        }),
                        size: "small",
                        variant: "outlined",
                        placeholder: "ДД.ММ.ГГГГ",
                        fullWidth: true,
                        error: Boolean(errors?.startDate),
                        helperText: errors?.startDate?.message,
                      },
                    }}
                  />
                  <div
                    style={{
                      width: "10px",
                      height: "1px",
                      background: "#CACACA",
                      borderRadius: 4,
                      marginBottom: "44px",
                    }}
                  />
                  <DatePicker
                    slots={{
                      openPickerIcon: DateRangeIcon,
                    }}
                    sx={{
                      ".MuiIconButton-root": {
                        color: "rgba(139, 139, 139, 1)",
                      },
                      minHeight: "84px",
                      width: "266px",
                    }}
                    value={dayjs(watch("endDate")) || null}
                    localeText={{
                      toolbarTitle: "ЧЧ.ММ.ГГГГ",
                      fieldDayPlaceholder: () => "ДД",
                      fieldMonthPlaceholder: () => "ММ",
                      fieldYearPlaceholder: () => "ГГГГ",
                    }}
                    onChange={(e: Dayjs | null) => {
                      dayjs(e).isValid() &&
                        setValue(
                          "endDate",
                          dayjs(e).format("YYYY-MM-DDTHH:mm:ssZ"),
                        )
                    }}
                    format="DD.MM.YYYY"
                    slotProps={{
                      textField: {
                        inputProps: register("endDate", {
                          required: "Это поле обязательно для заполнения",
                        }),
                        size: "small",
                        variant: "outlined",
                        placeholder: "ДД.ММ.ГГГГ",
                        fullWidth: true,
                        error: Boolean(errors?.endDate),
                        helperText: errors?.endDate?.message,
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          {!loading && (
            <Stack
              justifyContent={"space-between"}
              sx={{ width: "100%" }}
              direction="row"
              spacing={"10px"}
            >
              <Button
                size="large"
                sx={{ fontWeight: "bold" }}
                fullWidth
                variant="outlined"
                onClick={() => setOpen({ ...open, weekStatisticDialog: false })}
              >
                Отмена
              </Button>

              <Button
                size="large"
                sx={{ fontWeight: "bold" }}
                fullWidth
                variant="contained"
                type="submit"
              >
                Скачать
              </Button>
            </Stack>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default WeekStatisticDialog
